import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import {
  ArrowRight, Sun, Moon, ShieldCheck, Users2, KeyRound, Plug, BarChart3, MessagesSquare,
} from "lucide-react";

const FEATURES = [
  { icon: MessagesSquare, title: "Comunicazioni centralizzate", desc: "Gestisci tutte le conversazioni WhatsApp Business da un unico posto." },
  { icon: ShieldCheck, title: "Multi-tenant sicuro", desc: "Isolamento completo dei dati tra aziende, con controllo dei permessi." },
  { icon: Users2, title: "Team e ruoli", desc: "Owner, Admin, Manager, Operatore e Viewer con permessi granulari." },
  { icon: KeyRound, title: "API & Webhook", desc: "Chiavi API sicure e webhook pronti per l'integrazione." },
  { icon: Plug, title: "Integrazioni", desc: "Predisposto per Meta WhatsApp, Stripe, gestionali e altro." },
  { icon: BarChart3, title: "Statistiche in tempo reale", desc: "Dashboard con KPI, grafici e attività recenti." },
];

export default function Landing() {
  const { theme, toggle } = useTheme();
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <Logo />
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} className="rounded-full">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Link to="/login"><Button variant="ghost" data-testid="landing-login">Accedi</Button></Link>
            <Link to="/register"><Button className="rounded-lg" data-testid="landing-register">Inizia gratis</Button></Link>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-20 text-center md:py-28">
        <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-4 py-1.5 text-xs font-medium text-muted-foreground">
          <span className="h-2 w-2 rounded-full bg-primary" /> Business messaging, connected.
        </div>
        <h1 className="mx-auto max-w-3xl font-heading text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
          La piattaforma per le tue comunicazioni <span className="text-primary">WhatsApp Business</span>
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
          Connectivity centralizza contatti, conversazioni, campagne e automazioni.
          Multi-tenant, sicura e pronta per crescere con la tua azienda.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/register"><Button size="lg" className="w-full rounded-lg sm:w-auto" data-testid="landing-cta">
            Crea il tuo account <ArrowRight className="ml-1 h-4 w-4" />
          </Button></Link>
          <Link to="/login"><Button size="lg" variant="outline" className="w-full rounded-lg sm:w-auto">Accedi</Button></Link>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-xl border border-border bg-card p-6 transition-transform hover:-translate-y-1">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 text-sm text-muted-foreground sm:flex-row">
          <Logo />
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-foreground">Termini</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
          </div>
          <p>© 2026 Connectivity</p>
        </div>
      </footer>
    </div>
  );
}
